"use client";

import { CartItem, CartItemWithGroup, Cart } from "@/types";
import { CartSheetSkeleton } from "@/components/cart/cart-skeleton";

import { useMemo, useState, useEffect, useRef } from "react";
import { useCart } from "@/components/cart/cart-provider";
import { cn } from "@/lib/utils";
import { AnimatePresence } from "framer-motion";
import SingleCartItem from "./cart-item";
import EmptyCart from "./empty-cart";

interface CartItemsProps {
  isSheet?: boolean;
  isLoading?: boolean;
}

// Extend CartItemWithGroup locally to include addedAt for sorting
interface CartItemWithAddedAt extends CartItemWithGroup {
  addedAt?: number;
}

// Group cart items by product_id and decal_number
function groupCartItems(cartItems: CartItem[]): Record<string, CartItem[]> {
  const groups: Record<string, CartItem[]> = {};

  // Iterate through cart items
  cartItems.forEach((item) => {
    // Create a unique key for each item based on part_number and decal_number
    const key = item.decal_number
      ? `${item.part_number}-${item.decal_number}`
      : item.part_number;

    // Initialize array if needed
    if (!groups[key]) {
      groups[key] = [];
    }

    // Add item to its group
    groups[key].push(item);
  });

  return groups;
}

// Function to get cart data directly from localStorage
function getLocalCartData(): CartItem[] {
  if (typeof window === "undefined") return [];

  try {
    const cartData = localStorage.getItem("active-cart-data");
    if (!cartData) return [];

    const parsedCart = JSON.parse(cartData);
    return Array.isArray(parsedCart.cart_items) ? parsedCart.cart_items : [];
  } catch (error) {
    console.error("[CartItems] Error reading from localStorage:", error);
    return [];
  }
}

export function CartItems({
  isSheet = false,
  isLoading = false,
}: CartItemsProps) {
  const [isMounted, setIsMounted] = useState(false);
  const { carts, activeQuoteNumber, bulkCartOperation } = useCart();
  const initialItemsRef = useRef(getLocalCartData());

  // Get the active cart's items
  const items = useMemo(() => {
    // If we have context items, use them
    if (activeQuoteNumber) {
      const activeCart = carts.find(
        (cart: Cart) => cart.quote_number === activeQuoteNumber
      );
      if (activeCart && Array.isArray(activeCart.cart_items)) {
        return activeCart.cart_items;
      }
    }

    // Otherwise, if mounted, try localStorage
    if (isMounted && typeof window !== "undefined") {
      try {
        const localCartData = localStorage.getItem("active-cart-data");
        if (localCartData) {
          const parsedCart = JSON.parse(localCartData);
          if (parsedCart && Array.isArray(parsedCart.cart_items)) {
            return parsedCart.cart_items;
          }
        }
      } catch (error) {
        console.error("[CartItems] Error reading from localStorage:", error);
      }
    }

    // Fall back to initialItems from ref (prevents empty flicker)
    return initialItemsRef.current;
  }, [carts, activeQuoteNumber, isMounted]);

  // Group items by part_number and decal_number
  const groupedItems = useMemo(() => {
    const groups = groupCartItems(items);

    // Instead of adding direct group references, create a modified approach
    // that won't cause circular references
    const result: Record<string, CartItemWithAddedAt[]> = {};

    // Build a map of item to its index for fallback ordering
    const itemIndexMap = new Map<CartItemWithAddedAt, number>();
    items.forEach((item: CartItem, idx: number) => {
      itemIndexMap.set(item as CartItemWithAddedAt, idx);
    });

    // Process each group
    Object.entries(groups).forEach(([key, group]) => {
      // Create an array for this group without circular references
      const groupItems = group.map((item) => {
        const itemWithMeta = item as CartItemWithAddedAt;

        // Add the quote number without adding circular references
        if (activeQuoteNumber) {
          itemWithMeta._quoteNumber = activeQuoteNumber;
        } else if (typeof window !== "undefined") {
          // If no active quote number, try to get from localStorage
          try {
            const localCartData = localStorage.getItem("active-cart-data");
            if (localCartData) {
              const parsedCart = JSON.parse(localCartData);
              if (parsedCart && parsedCart.quote_number) {
                itemWithMeta._quoteNumber = parsedCart.quote_number;
              }
            }
          } catch (error) {
            console.error(
              "[CartItems] Error reading from localStorage:",
              error
            );
          }
        }

        // Fallback: If addedAt is missing, use the item's index (newest = highest index)
        if (!itemWithMeta.addedAt) {
          itemWithMeta.addedAt = itemIndexMap.get(itemWithMeta) ?? 0;
        }

        return itemWithMeta;
      });

      // Store the processed group
      result[key] = groupItems;
    });

    // Assign group references only in memory, not affecting serialization
    Object.values(result).forEach((group) => {
      group.forEach((item) => {
        // This circular reference is only used for rendering
        // and won't be serialized to localStorage
        if (isMounted) {
          item._group = group;
        }
      });
    });

    // Sort group keys by the LATEST addedAt timestamp in each group (newest first)
    const sortedKeys = Object.keys(result).sort((a, b) => {
      const aMax = Math.max(...result[a].map((i) => i.addedAt || 0));
      const bMax = Math.max(...result[b].map((i) => i.addedAt || 0));
      return bMax - aMax; // Descending: newest first
    });

    // Return a new object with sorted keys
    const sortedResult: Record<string, CartItemWithAddedAt[]> = {};
    sortedKeys.forEach((key) => {
      sortedResult[key] = result[key];
    });
    return sortedResult;
  }, [items, activeQuoteNumber, isMounted]);

  // Set mounted state on first render
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Only show skeleton if:
  // 1. Explicitly loading AND we have no items (either from context or localStorage)
  // 2. Not mounted AND we have no initial items from localStorage
  const shouldShowSkeleton =
    (isLoading && items.length === 0) ||
    (!isMounted && initialItemsRef.current.length === 0);

  if (shouldShowSkeleton) {
    return <CartSheetSkeleton />;
  }

  return (
    <div className={cn("space-y-8", isSheet ? "p-0" : "pt-2 md:pb-10")}>
      {items.length === 0 && !isLoading ? (
        <EmptyCart isInSheet={true} />
      ) : (
        <AnimatePresence>
          {Object.entries(groupedItems).map(([itemKey, itemGroup]) => (
            <SingleCartItem
              key={itemKey}
              item={itemGroup[0]}
              quantity={itemGroup.length}
              isSheet={isSheet}
              bulkCartOperation={bulkCartOperation}
            />
          ))}
        </AnimatePresence>
      )}
    </div>
  );
}
